import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../../utils/content.util';
import { useNavigate, useParams } from 'react-router-dom';
import { Dealer, DealerEmailTemplate, DealerProfileFile } from '../../../models/dealer.model';
import type { DealerAuditEntry } from '../../../models/dealerAudit.model';
import { onError } from '../../../utils/error.util';
import {
  createDealer,
  getDealer,
  getDealerAuditLog,
  getDealerProfileUploadUrl,
  updateDealer,
} from '../../../services/dealers.service';

import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { FieldArray, Form, Formik } from 'formik';
import { DealerSchema, dealerInitialValues } from './validationSchema';
import { hasPermission, Permission } from '../../../models/role.model';
import { CreateDealerRequest } from '../../../models/createDealer.model';
import { isDev } from '../../../utils/utils';
import { InputBoxPhoneMui } from '../../../components/form-elements/inputbox-phone-mui.component';
import { copyDealerLink } from '../dealers/dealer.container';
import { Tooltip } from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dealer-tabpanel-${index}`}
      aria-labelledby={`dealer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function formatAuditTimestamp(ts: number): string {
  return new Date(ts).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function auditEntrySummary(entry: DealerAuditEntry): string {
  const who = entry.actorDisplayName || 'Someone';
  const when = formatAuditTimestamp(entry.timestamp);
  switch (entry.action) {
    case 'created':
      return `Dealer was created by ${who} on ${when}`;
    case 'archived':
      return `${who} archived this dealer on ${when}`;
    case 'unarchived':
      return `${who} unarchived this dealer on ${when}`;
    case 'deleted':
      return `${who} deleted this dealer on ${when}`;
    case 'updated':
      if (entry.changedFields && entry.changedFields.length > 0) {
        const fields = entry.changedFields.join(', ');
        return `${who} updated ${fields} on ${when}`;
      }
      return `${who} updated this dealer on ${when}`;
    default:
      return `${who} — ${when}`;
  }
}

function formatChangeValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val !== 'object') return String(val);
  if (Array.isArray(val)) {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  const obj = val as Record<string, unknown>;
  if (obj.addressLine1 != null || obj.city != null) {
    const parts = [
      obj.addressLine1,
      [obj.city, obj.state].filter(Boolean).join(', '),
      obj.zipCode,
    ].filter(Boolean);
    return parts.join(' · ') || JSON.stringify(obj);
  }
  return JSON.stringify(obj);
}

function getUploaderDisplayName(currentUser: { given_name?: string; family_name?: string } | undefined): string {
  if (!currentUser) return 'Unknown';
  return `${currentUser.given_name ?? ''} ${currentUser.family_name ?? ''}`.trim() || 'Unknown';
}

function formatUploadedAt(ms: number | undefined): string {
  if (ms == null) return '';
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function normalizeDealerProfileDocuments(docs: DealerProfileFile[]) {
  return docs.map((d) => ({
    fileName: d.fileName,
    fileUrl: d.fileUrl,
    publicUrl: d.publicUrl,
    uploadedBy: d.uploadedBy ?? 'Unknown',
    uploadedBySub: d.uploadedBySub ?? '',
    uploadedAt: d.uploadedAt ?? 0,
  }));
}

function isAllowedDealerProfileFile(file: File): boolean {
  if (!/\.(jpe?g|png|pdf)$/i.test(file.name)) return false;
  if (
    file.type &&
    file.type !== 'image/jpeg' &&
    file.type !== 'image/png' &&
    file.type !== 'application/pdf'
  ) {
    return false;
  }
  return true;
}

function auditEntryChanges(entry: DealerAuditEntry): React.ReactNode {
  if (entry.action !== 'updated' || !entry.changes || Object.keys(entry.changes).length === 0)
    return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
      {Object.entries(entry.changes).map(([field, { from, to }]) => (
        <li key={field}>
          <Typography variant="body2" component="span" color="text.secondary">
            {field}: {formatChangeValue(from)} → {formatChangeValue(to)}
          </Typography>
        </li>
      ))}
    </Box>
  );
}

export default function DealerFormContainer() {
  const { isAuthenticated, currentUser } = useAppContext();

  const navigate = useNavigate();

  const { dealerId } = useParams();
  const [dealer, setDealer] = useState<Dealer>(dealerInitialValues);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [auditLog, setAuditLog] = useState<DealerAuditEntry[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    async function onLoad() {
      if (!isAuthenticated || !dealerId) {
        setIsLoading(false);
        return;
      }

      if (dealerId === 'new') {
        // new dealer
        setIsLoading(false);
        return;
      }

      try {
        const response = await getDealer(dealerId);

        setDealer({ ...response, emailTemplate: response.emailTemplate || DealerEmailTemplate.Default });
      } catch (e) {
        onError(e);
      }

      setIsLoading(false);
    }

    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const showActivityTab = dealerId && dealerId !== 'new';
  const canViewAuditLog = currentUser && hasPermission(currentUser, Permission.VIEW_DEALER_LOGS);
  const showActivityTabWithPermission = Boolean(showActivityTab && canViewAuditLog);
  const activityTabIndex =
    currentUser && hasPermission(currentUser, Permission.CUSTOM_DEALER_THEME) ? 2 : 1;

  useEffect(() => {
    if (
      !showActivityTabWithPermission ||
      dealerId === undefined ||
      tabValue !== activityTabIndex
    )
      return;
    let cancelled = false;
    setAuditLogLoading(true);
    getDealerAuditLog(dealerId, { limit: 50 })
      .then((res) => {
        if (!cancelled) setAuditLog(res.items);
      })
      .catch((e) => {
        if (!cancelled) onError(e);
      })
      .finally(() => {
        if (!cancelled) setAuditLogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealerId, showActivityTabWithPermission, tabValue, activityTabIndex]);

  // Check if user can edit: UPDATE_DEALER for existing dealers, CREATE_DEALER for new dealers
  const canEdit =
    currentUser &&
    ((dealerId === 'new' && hasPermission(currentUser, Permission.CREATE_DEALER)) ||
      (dealerId !== 'new' && hasPermission(currentUser, Permission.UPDATE_DEALER)));

  const canManageDealerProfileDocuments = Boolean(
    currentUser && hasPermission(currentUser, Permission.MANAGE_DEALER_DOCUMENTS)
  );

  const onSubmit = async (values: any) => {
    // Prevent submission if user doesn't have permission
    if (!canEdit) {
      alert('You do not have permission to perform this action');
      return;
    }
    setIsSubmitting(true);
    try {
      const documents = normalizeDealerProfileDocuments(values.documents ?? []);
      if (dealer.id) {
        const payload: Dealer = {
          ...dealer,
          ...values,
          documents,
        };

        await updateDealer(payload);
      } else {
        const payload: CreateDealerRequest = {
          ...values,
          documents,
        };

        const newDealer = await createDealer(payload);
        navigate(`/dealers/${newDealer.id}`);
      }
    } catch (err) {
      onError(err);
      alert('Something wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  async function handleProfileFileSelect(
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: DealerProfileFile[]) => void,
    currentDocuments: DealerProfileFile[]
  ) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (files.length === 0) return;
    const rejected = files.filter((f) => !isAllowedDealerProfileFile(f));
    if (rejected.length > 0) {
      onError(new Error('Only JPG, PNG, or PDF files are allowed.'));
      return;
    }
    const uploadedBy = getUploaderDisplayName(currentUser);
    const uploadedBySub = currentUser?.sub ?? '';
    const uploadedAt = Date.now();
    try {
      setIsUploading(true);
      const entityId = dealerId && dealerId !== 'new' ? dealerId : undefined;
      const uploadOne = async (file: File): Promise<DealerProfileFile> => {
        const { uploadUrl, fileKey, publicUrl } = await getDealerProfileUploadUrl(file.name, entityId);
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!putRes.ok) throw new Error(`Upload failed for ${file.name}`);
        return {
          fileName: file.name,
          fileUrl: fileKey,
          publicUrl,
          uploadedBy,
          uploadedBySub,
          uploadedAt,
        };
      };
      const newDocs = await Promise.all(files.map(uploadOne));
      setFieldValue('documents', [...currentDocuments, ...newDocs]);
    } catch (err: unknown) {
      onError(err);
    } finally {
      setIsUploading(false);
    }
  }

  function handleViewProfileFile(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function renderDealer() {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Formik
          enableReinitialize
          initialValues={dealer}
          validationSchema={DealerSchema}
          onSubmit={async (values, { resetForm }) => {
            await onSubmit(values);
            setDealer(values); // This will reset dirty to false, using the current values as the new initial values
          }}
        >
          {({ handleChange, handleBlur, values, touched, errors, setFieldValue, dirty }) => (
            <Form noValidate>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="dealer form tabs">
                  <Tab label="Company Information" id="dealer-tab-0" aria-controls="dealer-tabpanel-0" />
                  {currentUser && hasPermission(currentUser, Permission.CUSTOM_DEALER_THEME) ? (
                    <Tab label="Custom Theme" id="dealer-tab-1" aria-controls="dealer-tabpanel-1" />
                  ) : null}
                  {showActivityTabWithPermission ? (
                    <Tab
                      label="Activity"
                      id={`dealer-tab-${activityTabIndex}`}
                      aria-controls={`dealer-tabpanel-${activityTabIndex}`}
                    />
                  ) : null}
                </Tabs>
              </Box>
              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      name="companyName"
                      label="Company Name"
                      variant="outlined"
                      fullWidth
                      value={values.companyName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.companyName && Boolean(errors.companyName)}
                      helperText={touched.companyName && errors.companyName}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="address.addressLine1"
                      label="Address Line 1"
                      variant="outlined"
                      fullWidth
                      value={values.address?.addressLine1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.address?.addressLine1 && Boolean(errors.address?.addressLine1)}
                      helperText={touched.address?.addressLine1 && errors.address?.addressLine1}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="address.addressLine2"
                      label="Address Line 2"
                      variant="outlined"
                      fullWidth
                      value={values.address?.addressLine2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.address?.addressLine2 && Boolean(errors.address?.addressLine2)}
                      helperText={touched.address?.addressLine2 && errors.address?.addressLine2}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="address.city"
                      label="City"
                      variant="outlined"
                      fullWidth
                      value={values.address?.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.address?.city && Boolean(errors.address?.city)}
                      helperText={touched.address?.city && errors.address?.city}
                    />
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <TextField
                      name="address.state"
                      label="State"
                      variant="outlined"
                      fullWidth
                      value={values.address?.state}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.address?.state && Boolean(errors.address?.state)}
                      helperText={touched.address?.state && errors.address?.state}
                    />
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <TextField
                      name="address.zipCode"
                      label="Zip Code"
                      variant="outlined"
                      fullWidth
                      value={values.address?.zipCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.address?.zipCode && Boolean(errors.address?.zipCode)}
                      helperText={touched.address?.zipCode && errors.address?.zipCode}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <InputBoxPhoneMui
                      label="Phone"
                      name="phone"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      errorFeedback={errors.phone}
                      isValid={touched.phone && !errors.phone}
                      isInvalid={touched.phone && errors.phone !== undefined}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="websiteUrl"
                      label="Website"
                      variant="outlined"
                      fullWidth
                      value={values.websiteUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.websiteUrl && Boolean(errors.websiteUrl)}
                      helperText={touched.websiteUrl && errors.websiteUrl}
                    />
                  </Grid>
                  {currentUser && hasPermission(currentUser, Permission.CUSTOM_DEALER_EMAIL_TEMPLATE) ? (
                    <Grid size={{ xs: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel id="emailTemplate-label">Email Template</InputLabel>
                        <Select
                          labelId="emailTemplate-label"
                          name="emailTemplate"
                          value={values.emailTemplate}
                          label="Email Template"
                          onChange={(e) => setFieldValue('emailTemplate', e.target.value)}
                          onBlur={handleBlur}
                          error={touched.emailTemplate && Boolean(errors.emailTemplate)}
                        >
                          {Object.values(DealerEmailTemplate).map((template) => (
                            <MenuItem key={template} value={template}>
                              {template}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.emailTemplate && errors.emailTemplate && (
                          <FormHelperText error>{errors.emailTemplate}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  ) : null}
                  <Grid size={{ xs: 12 }}>
                    <FieldArray
                      name="emailAddresses"
                      render={(arrayHelpers) => (
                        <div>
                          {values.emailAddresses.map((email, index) => (
                            <div key={index}>
                              <TextField
                                name={`emailAddresses[${index}]`}
                                label="Email Address"
                                variant="outlined"
                                fullWidth
                                value={email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.emailAddresses && Boolean(errors.emailAddresses)}
                                helperText={touched.emailAddresses && errors.emailAddresses}
                              />
                              {values.emailAddresses.length > 1 && (
                                <Button type="button" onClick={() => arrayHelpers.remove(index)}>
                                  Remove
                                </Button>
                              )}
                              {index === values.emailAddresses.length - 1 && (
                                <Button
                                  type="button"
                                  onClick={() => arrayHelpers.push('')} // add an email to the list
                                >
                                  Add Email
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    />
                  </Grid>

                  {canManageDealerProfileDocuments ? (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Documents
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        JPG, PNG, or PDF only.
                      </Typography>
                      {(values.documents ?? []).length > 0 && (
                        <List
                          dense
                          disablePadding
                          sx={{
                            mb: 2,
                            '& .MuiListItem-root': {
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1,
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          {(values.documents ?? []).map((doc, idx) => (
                            <ListItem
                              key={`${doc.fileUrl}-${idx}`}
                              alignItems="center"
                              secondaryAction={
                                canEdit ? (
                                  <Box component="span" sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      aria-label="View file"
                                      onClick={() => handleViewProfileFile(doc.publicUrl ?? doc.fileUrl)}
                                    >
                                      <VisibilityOutlinedIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      aria-label="Remove file"
                                      onClick={() => {
                                        const next = (values.documents ?? []).filter((_, i) => i !== idx);
                                        setFieldValue('documents', next);
                                      }}
                                      color="error"
                                    >
                                      <DeleteOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <IconButton
                                    size="small"
                                    aria-label="View file"
                                    onClick={() => handleViewProfileFile(doc.publicUrl ?? doc.fileUrl)}
                                  >
                                    <VisibilityOutlinedIcon fontSize="small" />
                                  </IconButton>
                                )
                              }
                            >
                              <InsertDriveFileOutlinedIcon
                                sx={{ color: 'text.secondary', mr: 1.5, fontSize: 22 }}
                              />
                              <ListItemText
                                primary={doc.fileName}
                                secondary={
                                  doc.uploadedBy || doc.uploadedAt
                                    ? `Uploaded by ${doc.uploadedBy ?? 'Unknown'}${doc.uploadedAt ? ` on ${formatUploadedAt(doc.uploadedAt)}` : ''}`
                                    : undefined
                                }
                                slotProps={{
                                  primary: {
                                    variant: 'body2',
                                    sx: { fontWeight: 500 },
                                    noWrap: true,
                                  },
                                  secondary: {
                                    variant: 'caption',
                                    color: 'text.secondary',
                                  },
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => handleProfileFileSelect(e, setFieldValue, values.documents ?? [])}
                      />
                      {canEdit ? (
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading || isSubmitting}
                        >
                          {isUploading ? 'Uploading...' : 'Add files'}
                        </Button>
                      ) : null}
                      {canEdit && isUploading ? (
                        <CircularProgress size={22} sx={{ ml: 2, verticalAlign: 'middle' }} />
                      ) : null}
                    </Grid>
                  ) : null}
                </Grid>
              </TabPanel>

              {currentUser && hasPermission(currentUser, Permission.CUSTOM_DEALER_THEME) ? (
                <TabPanel value={tabValue} index={1}>
                  <TextField
                    name="customThemeContent"
                    label="Custom Theme Content"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={25}
                    value={values.customThemeContent}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.customThemeContent && Boolean(errors.customThemeContent)}
                    helperText={touched.customThemeContent && errors.customThemeContent}
                  />
                </TabPanel>
              ) : null}

              {showActivityTabWithPermission ? (
                <TabPanel value={tabValue} index={activityTabIndex}>
                  <Typography variant="h6" gutterBottom>
                    Audit log
                  </Typography>
                  {auditLogLoading ? (
                    <CircularProgressComponent />
                  ) : auditLog.length === 0 ? (
                    <Typography color="text.secondary">No activity yet.</Typography>
                  ) : (
                    <List dense disablePadding>
                      {auditLog.map((entry, idx) => (
                        <ListItem
                          key={`${entry.timestamp}-${idx}`}
                          alignItems="flex-start"
                          disableGutters
                          sx={{ flexDirection: 'column', alignItems: 'stretch' }}
                        >
                          <ListItemText
                            primary={auditEntrySummary(entry)}
                            secondary={auditEntryChanges(entry)}
                            slotProps={{
                              primary: { variant: 'body2' },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </TabPanel>
              ) : null}

              <Grid size={{ xs: 12 }} sx={{ display: 'flex', gap: 1, my: 2 }}>
                {canEdit ? (
                  <Button variant="contained" type="submit" disabled={isSubmitting || !dirty}>
                    Save
                  </Button>
                ) : undefined}
                <Button variant="outlined" onClick={() => navigate('/dealers')}>
                  Close
                </Button>
                {dealer.id && (
                  <Button variant="outlined" onClick={() => copyDealerLink(dealer.id)}>
                    <Tooltip title="Copy Dealer Link">
                      <ContentCopyIcon color="primary" fontSize="small" sx={{ cursor: 'pointer' }} />
                    </Tooltip>
                  </Button>
                )}
              </Grid>

              {isDev ? (
                <>
                  <div>
                    <p>Initial Values:</p>
                    <code>{JSON.stringify(dealer, null, 2)}</code>
                  </div>
                  <div>
                    <p>Errors:</p>
                    <code>{JSON.stringify(errors, null, 2)}</code>
                  </div>
                  <div>
                    <p>Dirty:</p>
                    <code>{JSON.stringify(dirty, null, 2)}</code>
                  </div>
                </>
              ) : null}
            </Form>
          )}
        </Formik>
      </Container>
    );
  }

  return (
    <>
      <div className="dealers">
        <h2 className="pb-2 mt-4 mb-3 border-bottom">{dealerId === 'new' ? 'Add New Dealer' : 'Edit Dealer'}</h2>
      </div>
      {isLoading ? <CircularProgressComponent /> : renderDealer()}
    </>
  );
}
